"use client";
import {
  RefineSnackbarProvider,
  useNotificationProvider,
} from "@refinedev/mui";
import { type I18nProvider, Refine } from "@refinedev/core";
import { RefineKbar, RefineKbarProvider } from "@refinedev/kbar";
import routerProvider from "@refinedev/nextjs-router";
import React, { Suspense, type PropsWithChildren } from "react";
import { ColorModeContextProvider } from "@contexts/color-mode";
import { dataProvider } from "@providers/data-provider";
import { authProviderClient } from "@providers/auth-provider/auth-provider.client";
import { useLocale, useTranslations } from "next-intl";
import { setUserLocale } from "@i18n";
import { mockProvider } from "@providers/data-provider/mockdata";
import AttributionIcon from "@mui/icons-material/Attribution";
import { gqldataProvider } from "@providers/data-provider/graphql";
import { restapidataProvider } from "@providers/data-provider/restapi";
import { CalendarIcon } from "@mui/x-date-pickers";
import GridViewIcon from "@mui/icons-material/GridView";

type Props = {
  defaultMode?: string;
};

export const RefineContext = ({
  defaultMode,
  children,
}: PropsWithChildren<Props>) => {
  const t = useTranslations();

  const i18nProvider: I18nProvider = {
    translate: (key: string, options: any) => t(key, options),
    getLocale: useLocale,
    changeLocale: setUserLocale,
  };

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <RefineKbarProvider>
        <ColorModeContextProvider defaultMode={defaultMode}>
          <RefineSnackbarProvider>
            <Refine
              routerProvider={routerProvider}
              dataProvider={{
                default: mockProvider,
                template: dataProvider,
                restapi: restapidataProvider,
                gql: gqldataProvider,
              }}
              notificationProvider={useNotificationProvider}
              authProvider={authProviderClient}
              i18nProvider={i18nProvider}
              resources={[
                {
                  name: "students",
                  list: "/students",
                  create: "/students/create",
                  edit: "/students/edit/:id",
                  show: "/students/show/:id",
                  meta: {
                    icon: <AttributionIcon />,
                    canDelete: true,
                  },
                },
                {
                  name: "schoolyears",
                  list: "/schoolyears",
                  create: "/schoolyears/create",
                  edit: "/schoolyears/edit/:id",
                  show: "/schoolyears/show/:id",
                  meta: {
                    icon: <CalendarIcon />,
                    canDelete: true,
                  },
                },
                {
                  name: "section-scores",
                  list: "/section-scores",
                  meta: {
                    icon: <GridViewIcon />,
                    label: t("sectionScores.sectionScores"),
                  },
                },
              ]}
              options={{
                syncWithLocation: true,
                warnWhenUnsavedChanges: true,
                projectId: "xaLwJq-kmYU2D-dIFubj",
                redirect: {
                  afterEdit: false,
                },
              }}
            >
              {children}
              <RefineKbar />
            </Refine>
          </RefineSnackbarProvider>
        </ColorModeContextProvider>
      </RefineKbarProvider>
    </Suspense>
  );
};
